import { resolveId } from '@medplum/core'
import {
  decideRefillActions,
  type RefillMedLine,
} from '@seen/core/medication-refill/decideRefillActions'
import { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { useMemo } from 'react'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

interface UseMedicationRefillVisitLinesResult {
  meds: readonly RefillMedLine[]
  medicationById: ReadonlyMap<string, ExtendedMedicationRequest>
  isLoading: boolean
  hasParticipant: boolean
  participantId?: string
}

// FHIR reads + decideRefillActions for the visit-note block. Does not write
// tasks — filing stays on the pilot script / flagged cron. The note is the
// visit, so listForVisitNote skips the cron's own-PCP appointment gate.
export function useMedicationRefillVisitLines(
  compositionReferenceString: string | null | undefined
): UseMedicationRefillVisitLinesResult {
  const { data: composition, isLoading: isLoadingComposition } = useRead(
    ExtendedComposition,
    compositionReferenceString
  )

  const participant = composition?.patientSubject

  const { data: medications, isLoading: isLoadingMeds } =
    useSearchResourcePages(
      ExtendedMedicationRequest,
      participant
        ? ExtendedMedicationRequest.queryActiveByPatient(participant)
        : undefined
    )

  const meds = useMemo(() => {
    const participantId = resolveId(participant)
    if (!participantId || !medications) {
      return []
    }
    const { visitNoteLines } = decideRefillActions({
      // Live instant, not date-only midnight: the engine reads the clinic
      // civil day from it, and UTC midnight is yesterday in Pacific.
      today: new Date(),
      medications,
      ownPcpVisitsByParticipant: new Map(),
      listForVisitNote: true,
    })
    return (
      visitNoteLines.find((line) => line.participantId === participantId)
        ?.meds ?? []
    )
  }, [medications, participant])

  const medicationById = useMemo(() => {
    const byId = new Map<string, ExtendedMedicationRequest>()
    for (const medication of medications ?? []) {
      if (medication.id) {
        byId.set(medication.id, medication)
      }
    }
    return byId
  }, [medications])

  return {
    meds,
    medicationById,
    isLoading: isLoadingComposition || (!!participant && isLoadingMeds),
    hasParticipant: !!participant,
    participantId: resolveId(participant),
  }
}
